/**
 * AsciiArt.ts — ASCII art library for Gallowcrest.
 *
 * Contains Gallowcrest-specific art alongside shared base pieces.
 */

import type { Terminal } from './Terminal';
import type { ColorName } from './ColorTheme';

// --- Art Library ---

export const ART = {
  title: `
╔═══════════════════════════════════════════╗
║      G  A  L  L  O  W  C  R  E  S  T     ║
╚═══════════════════════════════════════════╝`,

  subtitle: `       ═══ Dark Fantasy RPG ═══`,

  tagline: `        A ChoiceRealms Adventure`,

  frostcrown: `
     *              .  *  .              *
                       /\\
                /\\    /  \\    /\\
           /\\  /  \\  /    \\  /  \\  /\\
          /  \\/    \\/      \\/    \\/  \\
     ____/                            \\____
     ════════════════════════════════════════`,

  castle: `
              |>>>
              |
        |>>>  |  |>>>
        |     |  |
    |>>>|     |  |     |>>>
    |   |     |  |     |   |
    |   |  ___|__|___  |   |
    |   | |         | |   |
    |___|_|_________|_|___|
   /                       \\
  /_____ _____ _____ _____  \\`,

  sword: `
        /\\
       /  \\
      / ** \\
     / *  * \\
    /________\\
        ||
        ||
        ||
       /||\\
      / || \\
        \\/`,

  skull: `
       ______
      /      \\
     |  x  x  |
     |   __   |
      \\ |==| /
       '----'`,

  wolf: `
         /\\     /\\
        {  \\   /  }
         \\  \\_/  /
      .--'       '--.
     /    .     .    \\
    |    (o)   (o)    |
     \\     '---'     /
      '--.       .--'
          |     |
          |     |`,

  snowflake: `
        *
       /|\\
      / | \\
   --*--+--*--
      \\ | /
       \\|/
        *`,

  book: `
     ___________
    /           /|
   /           / |
  /___________/  |
  |           |  |
  | GALLOWCRS |  |
  |           | /
  |___________|/`,

  dragon: `
                ______________
          ,===:'.,            '-,
               ':.'-,googl       '-,
                 ':  ',googl       '.,
                  '. '  '.,googl    '-,
                    ; ':.. '.,googl   '-.
                    ':    '.,googl'     '-
                     ':   ':.googl       '.
                      ':    ':..g    _____'
                        ':     '. ,googl
                          ':     .-'googl
                            ': .'
                              '`,

  chapterDivider: `
  ════════════════════════════════════════════════════════════════`,

  separator: `──────────────────────────────────────────────────`,
};

// --- Renderer ---

export class AsciiArtRenderer {
  private terminal: Terminal;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  /** Render an ASCII art piece to the terminal. */
  render(artName: keyof typeof ART, color: ColorName = 'yellow'): void {
    const art = ART[artName];
    const lines = art.split('\n');

    const startIndex = lines[0]?.trim() === '' ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      this.terminal.writeLine(lines[i], color);
    }
  }

  /** Render ASCII art centered horizontally in the terminal. */
  renderCentered(artName: keyof typeof ART, color: ColorName = 'yellow', maxRows?: number): void {
    const art = ART[artName];
    const lines = art.split('\n');
    const { cols } = this.terminal.getSize();

    const startIndex = lines[0]?.trim() === '' ? 1 : 0;

    // Truncate to maxRows if specified
    const endIndex = maxRows ? Math.min(lines.length, startIndex + maxRows) : lines.length;

    // Calculate centering from FULL art width (not truncated) to prevent horizontal shift
    let maxLen = 0;
    for (let i = startIndex; i < lines.length; i++) {
      if (lines[i].length > maxLen) maxLen = lines[i].length;
    }

    const offset = Math.max(0, Math.floor((cols - maxLen) / 2));
    const padding = ' '.repeat(offset);

    for (let i = startIndex; i < endIndex; i++) {
      this.terminal.writeLine(`${padding}${lines[i]}`, color);
    }
  }

  /** Render a custom multi-line string as ASCII art. */
  renderCustom(art: string, color: ColorName = 'white'): void {
    const lines = art.split('\n');
    for (const line of lines) {
      this.terminal.writeLine(line, color);
    }
  }
}
